import { httpClient } from "../../lib/http/axios";
import type { ApiEnvelope } from "../../lib/http/contracts";
import type { GeoBusinessVerifyRequest, GeoBusinessVerifyResult } from "./types";

type RawCandidate = {
  provider: "amap" | "amap_geocode";
  poi_id: string;
  name: string;
  address: string;
  province: string;
  city: string;
  district: string;
  longitude: number;
  latitude: number;
  is_qualified: boolean;
  qualification_reason: string;
};

type RawResult = {
  scenario: GeoBusinessResult["scenario"];
  status: GeoBusinessResult["status"];
  conclusion: string;
  llm_conclusion: string;
  amap_standard_address: string;
  province: string;
  city: string;
  district: string;
  township?: string;
  longitude: number | null;
  latitude: number | null;
  coordinate_system: string;
  confidence_score: number;
  correction_reason: string;
  retryable: boolean;
  candidates: RawCandidate[];
};

type GeoBusinessResult = GeoBusinessVerifyResult;

const geoVerifyRequest = { timeout: 120_000, "axios-retry": { retries: 0 } } as const;

/** 调用后端统一业务 GEO 出口。 */
export async function verifyBusinessGeo(
  payload: GeoBusinessVerifyRequest,
  endpoint = "/geo/business/verify",
): Promise<GeoBusinessVerifyResult> {
  const response = await httpClient.post<ApiEnvelope<RawResult>>(
    endpoint,
    mapRequest(payload),
    geoVerifyRequest,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("GEO 业务核验接口返回了无效数据");
  }
  return mapResult(response.data.data);
}

/** 调用已保存业务对象的 GEO 编排入口，响应仍复用统一稳定 DTO。 */
export async function verifySavedBusinessGeo(
  endpoint: string,
): Promise<GeoBusinessVerifyResult> {
  const response = await httpClient.post<ApiEnvelope<RawResult>>(endpoint, {}, geoVerifyRequest);
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("GEO 业务核验接口返回了无效数据");
  }
  return mapResult(response.data.data);
}

/** 映射统一业务 GEO 请求为后端 snake_case DTO。 */
function mapRequest(payload: GeoBusinessVerifyRequest) {
  return {
    province: payload.province,
    city: payload.city ?? "",
    district: payload.district ?? "",
    area_name: payload.areaName,
    scenario: payload.scenario,
    context_name: payload.contextName ?? null,
    source_address: payload.sourceAddress ?? null,
    original_longitude: payload.originalLongitude ?? null,
    original_latitude: payload.originalLatitude ?? null,
    organization_site_count: payload.organizationSiteCount ?? null,
    point_sub_type: payload.pointSubType ?? null,
    short_name: payload.shortName ?? null,
    telecodes: payload.telecodes ?? [],
    iata_codes: payload.iataCodes ?? [],
    icao_codes: payload.icaoCodes ?? [],
    terminal_codes: payload.terminalCodes ?? [],
  };
}

/** 映射后端统一业务 GEO 结果。 */
function mapResult(raw: RawResult): GeoBusinessVerifyResult {
  return {
    scenario: raw.scenario,
    status: raw.status,
    conclusion: raw.conclusion,
    llmConclusion: raw.llm_conclusion,
    amapStandardAddress: raw.amap_standard_address,
    province: raw.province,
    city: raw.city,
    district: raw.district,
    township: raw.township ?? "",
    longitude: raw.longitude ?? undefined,
    latitude: raw.latitude ?? undefined,
    coordinateSystem: raw.coordinate_system,
    confidenceScore: raw.confidence_score,
    correctionReason: raw.correction_reason,
    retryable: raw.retryable,
    candidates: (raw.candidates ?? []).map((candidate) => ({
      provider: candidate.provider,
      poiId: candidate.poi_id,
      name: candidate.name,
      address: candidate.address,
      province: candidate.province,
      city: candidate.city,
      district: candidate.district,
      longitude: candidate.longitude,
      latitude: candidate.latitude,
      isQualified: candidate.is_qualified,
      qualificationReason: candidate.qualification_reason,
    })),
  };
}
